import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import MessageCard from './message-card/MessageCard';
import CommonLoader from '../../../../../components/common-loader/CommonLoader';
import EmptyMessageCard from '../../../../../scout-new/components/message-card/EmptyMessageCard';
import ConfirmationModal from '../../../../../components/confirmation-modal/ConfirmationModal';
import MessageInfoModal from '../../../../../scout-new/components/message-info-modal/MessageInfoModal';
import { ACTIV8_MESSAGE_TABS, ACTIV8_TABS } from '../../../../../constants';
import { changeInitialAdjustMessage, changeMessagesTab } from '../../../../../redux/activ8/activ8MessagesReducer';
import { useProject } from '../../../../../context/ProjectContext';
import { getActiv8MessageLogs } from '../../../../../redux/activ8/activ8MessagesActions';
import { changeSelectedMessage, clearBeforeInit } from '../../../../../redux/story/storyReducer';
import { changeActiv8Tab } from '../../../../../redux/activ8/activ8Reducer';

import scss from './Messages.module.scss';

function Messages({ bindNextAction, content, isLoading, isShowSaveButton = true }) {
  const { t } = useTranslation('activ8');
  const scrollableRef = useRef(null);
  const dispatch = useDispatch();
  const { actions } = useProject();
  const { id: projectId } = useParams();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const isAddMessageToStoryMode = useSelector((state) => state.story.isAddMessageToStoryMode);

  const handleCloseModals = () => {
    setIsDeleteModalOpen(false);
    setIsInfoModalOpen(false);
    setSelectedMessage({});
  };

  const handleOpenDeleteModal = (message) => {
    bindNextAction(() => {
      setSelectedMessage(message);
      setIsDeleteModalOpen(true);
    });
  };

  const handleOpenInfoModal = (message) => {
    setSelectedMessage(message);
    setIsInfoModalOpen(true);
  };

  const handleDeleteMessage = () => {
    actions('deleteMessage', { id: selectedMessage.id, savedId: selectedMessage.savedId });
  };

  const handleSaveMessage = ({ id, message, savedId }) => {
    if (!savedId) {
      bindNextAction(() => actions('saveMessage', { id, message, projectId }));
    }
  };

  const handleGoToStory = (message) => {
    bindNextAction(() => {
      if (isAddMessageToStoryMode) {
        dispatch(changeSelectedMessage(message.message));
        dispatch(changeActiv8Tab(ACTIV8_TABS.STORY));
        return;
      }

      dispatch(clearBeforeInit());
      dispatch(changeSelectedMessage(message.message));
      dispatch(changeActiv8Tab(ACTIV8_TABS.STORY));
    });
  };

  const handleAdjust = (message) => {
    bindNextAction(() => {
      dispatch(changeMessagesTab(ACTIV8_MESSAGE_TABS.ADJUST));
      dispatch(changeInitialAdjustMessage(message.message));
    });
  };

  return (
    <div className={scss.wrapper}>
      <div className={scss.messagesList} ref={scrollableRef}>
        {content.map((message) => (
          <MessageCard
            key={message.id}
            message={message}
            onDelete={handleOpenDeleteModal}
            onInfo={handleOpenInfoModal}
            onSave={handleSaveMessage}
            isShowSaveButton={isShowSaveButton}
            onGoToStory={handleGoToStory}
            goToStoryTooltip={isAddMessageToStoryMode ? t('addToStory') : t('initStory')}
            onAdjust={handleAdjust}
            bindNextAction={bindNextAction}
          />
        ))}

        {content.length === 0 ? <EmptyMessageCard /> : null}
      </div>

      <CommonLoader isLoading={isLoading} scrollableRef={scrollableRef} disableScrolling />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModals}
        onCancel={handleCloseModals}
        onConfirm={handleDeleteMessage}
        subLabel={t('doYouReallyWantToDelete')}
      />

      <MessageInfoModal
        isOpen={isInfoModalOpen}
        onClose={handleCloseModals}
        message={{ ...selectedMessage, id: selectedMessage.savedId }}
        selector={(state) => state.activ8Messages}
        getMessageLogs={getActiv8MessageLogs}
      />
    </div>
  );
}

export default Messages;
